"use client";

import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import dynamic from "next/dynamic";
const DynamicBentoGrid = dynamic(
    () => import("@/components/dynamic-bento-grid").then((m) => m.DynamicBentoGrid),
    {
        ssr: false,
        loading: () => <div className="w-full h-[80vh] min-h-[520px] bg-white/5 rounded-[24px] animate-pulse" />
    }
);
import Link from "next/link";
import { ArrowRight, ChevronDown, Download } from "lucide-react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";

// Three.js canvas — no SSR
const TreeCanvas = dynamic(
    () => import("@/components/TreeCanvas").then((m) => m.TreeCanvas),
    {
        ssr: false,
        loading: () => <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_70%,rgba(204,252,69,0.05)_0%,transparent_60%)]" />
    }
);

const LIME = "#ccfc45";

export default function Home() {
    const { resolvedTheme } = useTheme();
    const [mounted, setMounted] = useState(false);
    // Tree fade/scale driven by scroll progress (0–1)
    const [scrollP, setScrollP] = useState(0);
    const scrollDimensions = React.useRef({ height: 0, inner: 0 });

    useEffect(() => setMounted(true), []);

    useEffect(() => {
        const updateDimensions = () => {
            scrollDimensions.current = {
                height: document.documentElement.scrollHeight,
                inner: window.innerHeight
            };
        };
        updateDimensions();

        let rafId: number;
        const onScroll = () => {
            rafId = requestAnimationFrame(() => {
                const { height, inner } = scrollDimensions.current;
                const h = height - inner;
                if (h > 0) {
                    setScrollP(Math.min(window.scrollY / h, 1));
                }
            });
        };

        window.addEventListener("resize", updateDimensions);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("resize", updateDimensions);
            window.removeEventListener("scroll", onScroll);
            cancelAnimationFrame(rafId);
        };
    }, []);

    const isDark = mounted ? (resolvedTheme === "dark") : true;

    // Tree: fade out and shrink as user scrolls past 60% of page
    const treeProgress = Math.min(scrollP / 0.6, 1); // 0 at top, 1 at 60% scroll
    const treeOpacity = isDark ? (1 - treeProgress) : (1 - treeProgress) * 0.4;
    const treeScale = 1 - treeProgress * 0.12;

    // Theme-based colors (Defaulting to Dark)
    const bgColor = "bg-background";
    const subBgColor = "bg-surface";
    const textColor = "text-foreground";
    const subTextColor = "text-muted-foreground";
    const borderColor = "border-border-subtle";
    const cardBg = "bg-surface";

    return (
        <main
            className={cn(
                "min-h-screen w-full overflow-x-hidden md:h-screen md:w-screen md:overflow-hidden transition-colors duration-500 flex flex-col",
                "p-4 md:p-6 pt-24 md:pt-[84px]",
                bgColor,
                textColor
            )}
        >
            {/* JSON-LD Structured Data for SEO */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify([
                        {
                            "@context": "https://schema.org",
                            "@type": "SoftwareApplication",
                            "name": "Restaubee",
                            "applicationCategory": "BusinessApplication",
                            "operatingSystem": "Windows, Web",
                            "isAccessibleForFree": true,
                            "description": "Restaubee is the most advanced free universal POS and management system for restaurants, grocery, and retail.",
                        },
                        {
                            "@context": "https://schema.org",
                            "@type": "Organization",
                            "name": "Restaubee",
                            "url": "https://restaubee.com",
                            "sameAs": [
                                "https://twitter.com/restaubee",
                                "https://www.linkedin.com/company/restaubee",
                                "https://www.facebook.com/restaubee"
                            ]
                        }
                    ])
                }}
            />

            {!mounted ? null : (
                <div className={cn(
                    "w-full flex-1 md:h-full rounded-[2.5rem] border overflow-hidden relative",
                    "flex flex-col md:grid md:grid-cols-12 md:grid-rows-1",
                    "bg-surface shadow-[0_20px_60px_rgba(0,0,0,0.1)] transition-colors duration-500",
                    borderColor,
                    "divide-y md:divide-y-0 md:divide-x",
                    borderColor
                )}>
                    {/* ─────────── LEFT PANEL: Branding & Actions ─────────── */}
                    <div className="md:col-span-3 h-auto md:h-full flex flex-col justify-between p-8 md:p-10 relative z-20 bg-background/50 backdrop-blur-sm">
                        <div className="pt-2">
                            <motion.p 
                                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                                className="text-[10px] font-black uppercase tracking-[0.4em] mb-6" style={{ color: LIME }}
                            >
                                Universal Business Platform
                            </motion.p>
                            <motion.h1 
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                                className="text-5xl lg:text-7xl font-black tracking-tighter leading-[0.9] mb-8 text-foreground"
                            >
                                Modern Retail.<br />
                                <span style={{ color: LIME }}>Simplified.</span>
                            </motion.h1>
                            <motion.p 
                                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                                className="text-sm text-muted-foreground font-medium leading-relaxed pr-4"
                            >
                                The world's most versatile free POS software. Built for restaurants, grocery, pharmacies, and beyond. Zero subscription fees. Infinite scalability.
                            </motion.p>
                        </div>

                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                            className="flex flex-col gap-4 pb-4"
                        >
                            {/* ── Official Microsoft Store Link Button (Native UI) ── */}
                            <a
                                href="https://apps.microsoft.com/store/detail/9P9W500L6KWG?cid=DevShareMCLPCS"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "none" }}
                            >
                                <button className={cn(
                                    "w-full px-6 py-4 rounded-2xl font-bold border transition-all flex items-center justify-between group shadow-sm",
                                    "bg-foreground text-background hover:scale-[1.02]", borderColor
                                )}>
                                    <div className="flex flex-col items-start leading-tight text-left">
                                        <span className="text-[9px] uppercase tracking-widest opacity-60 mb-0.5">Download for Windows</span>
                                        <span className="text-[15px]">Microsoft Store</span>
                                    </div>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="20" height="20" aria-label="Microsoft logo" className="flex-shrink-0 group-hover:scale-110 transition-transform">
                                        <rect x="1"  y="1"  width="10" height="10" fill="#f25022" />
                                        <rect x="12" y="1"  width="10" height="10" fill="#7fba00" />
                                        <rect x="1"  y="12" width="10" height="10" fill="#00a4ef" />
                                        <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
                                    </svg>
                                </button>
                            </a>

                            {/* ── Secondary: Astor (Coming Soon) ── */}
                            <a
                                href="#"
                                style={{ textDecoration: "none", cursor: "default" }}
                            >
                                <button className={cn(
                                    "w-full px-6 py-4 rounded-2xl font-bold border transition-all flex items-center justify-between group",
                                    "text-foreground/70 bg-surface/50 cursor-not-allowed", borderColor
                                )}>
                                    <div className="flex flex-col items-start leading-tight text-left">
                                        <span className="text-[9px] uppercase tracking-widest text-primary mb-0.5 font-black">Coming Soon</span>
                                        <span className="text-[15px] opacity-80">Astor by Restaubee</span>
                                    </div>
                                    <div className="w-1.5 h-1.5 rounded-full bg-primary/50 animate-pulse" />
                                </button>
                            </a>
                        </motion.div>
                    </div>

                    {/* ─────────── CENTER PANEL: Immersive Tree ─────────── */}
                    <div className="md:col-span-6 relative min-h-[400px] md:min-h-0 md:h-full flex flex-col items-center justify-center overflow-hidden bg-background">
                        <div
                            className="absolute inset-0 z-0 pointer-events-none overflow-hidden"
                            style={{ background: "radial-gradient(circle at 50% 50%, rgba(204,252,69,0.05) 0%, transparent 60%)" }}
                        />
                        <div 
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[600px] md:h-[600px] lg:w-[700px] lg:h-[700px] rounded-full z-5 pointer-events-none transition-all duration-700"
                            style={{
                                background: "radial-gradient(circle at 40% 40%, rgba(30,41,59,0.9) 0%, rgba(15,23,42,0.95) 40%, rgba(0,0,0,0.85) 80%)",
                                border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.1)",
                                boxShadow: "inset 0 0 100px rgba(0,0,0,0.5)",
                                backdropFilter: "blur(40px)"
                            }}
                        />
                        <div 
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[500px] lg:h-[500px] rounded-full z-[6] pointer-events-none border animate-pulse opacity-10"
                            style={{ borderColor: "#ffffff" }}
                        />
                        <div className="absolute inset-0 z-10 pointer-events-none">
                            <TreeCanvas />
                        </div>
                        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 bg-background/80 backdrop-blur-xl border px-6 py-3 rounded-full flex items-center gap-3 shadow-lg" style={{ borderColor: 'var(--border-subtle)' }}>
                           <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: LIME }} />
                           <span className="text-[10px] font-black uppercase tracking-[0.2em] text-foreground">System Core Active</span>
                        </div>
                    </div>

                    {/* ─────────── RIGHT PANEL: Grid Partitions / Info ─────────── */}
                    <div className="md:col-span-3 h-auto md:h-full flex flex-col divide-y bg-background/50 backdrop-blur-sm overflow-hidden" style={{ borderColor: 'var(--border-subtle)' }}>
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="flex-1 p-5 md:p-6 lg:p-8 flex flex-col justify-center">
                            <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Architecture</p>
                            <p className="text-xl lg:text-2xl font-black tracking-tighter text-foreground mb-1.5">
                                Offline-First <span style={{ color: LIME }}>Design</span>
                            </p>
                            <p className="text-[12px] font-medium text-muted-foreground leading-relaxed hidden sm:block">Continuous sync tech ensures business never halts during outages.</p>
                        </motion.div>
                        
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="flex-1 p-5 md:p-6 lg:p-8 flex flex-col justify-center">
                            <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Hardware Connect</p>
                            <p className="text-xl lg:text-2xl font-black tracking-tighter text-foreground mb-1.5">
                                Native Print <span style={{ color: LIME }}>API</span>
                            </p>
                            <p className="text-[12px] font-medium text-muted-foreground leading-relaxed hidden sm:block">Direct local communication with thermal POS printers for zero-latency KOTs.</p>
                        </motion.div>
                        
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="flex-1 p-5 md:p-6 lg:p-8 flex flex-col justify-center">
                            <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Operation Sync</p>
                            <p className="text-xl lg:text-2xl font-black tracking-tighter text-foreground mb-1.5">
                                Realtime <span style={{ color: LIME }}>Flow</span>
                            </p>
                            <p className="text-[12px] font-medium text-muted-foreground leading-relaxed hidden sm:block">Instantly orchestrate orders from front-of-house to kitchen or warehouse.</p>
                        </motion.div>
                        
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} className="flex-[0.7] p-5 md:p-6 lg:p-8 bg-surface/50 flex flex-col justify-center overflow-hidden">
                            <p className="text-[9px] font-black uppercase tracking-[0.3em] text-muted-foreground mb-3">Core Modules</p>
                            <div className="flex flex-wrap gap-2">
                                {['Point of Sale', 'Inventory', 'Extensions', 'Cloud DB'].map((m) => (
                                    <span key={m} className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider rounded-lg border border-border-subtle bg-background text-foreground shadow-sm whitespace-nowrap">
                                        {m}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}
            

        </main>
    );
}
